import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Loader2, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/common/SearchableSelect';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { apiMessage } from '@/lib/pricing';

type LicenceKey = {
  id: string;
  prefix: string;
  label: string;
  created_at: string;
  created_by: string;
  last_used_at: string | null;
  revoked: boolean;
};

type Licence = {
  id: string;
  organization: string;
  keycloak_org_id: string;
  expires_on: string | null;
  status: 'active' | 'expired' | 'revoked';
  notes: string;
  created_at: string;
  created_by: string;
  keys: LicenceKey[];
};

type Customer = { keycloak_org_id: string; name: string };

const STATUS_STYLE: Record<Licence['status'], string> = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  expired: 'border-amber-200 bg-amber-50 text-amber-800',
  revoked: 'border-red-200 bg-red-50 text-red-700',
};

function when(value: string | null) {
  if (!value) return 'never';
  return new Date(value).toLocaleDateString();
}

export default function OnPremAocPage() {
  const queryClient = useQueryClient();
  const [issuing, setIssuing] = React.useState(false);
  const [org, setOrg] = React.useState('');
  const [expires, setExpires] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [secret, setSecret] = React.useState<{ key: string; who: string } | null>(null);

  const board = useQuery({
    queryKey: ['on-prem-licences'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/on-prem-licences/');
      return res.data as { licences: Licence[]; organizations: Customer[] };
    },
  });

  const licences = { isLoading: board.isLoading, data: board.data?.licences };
  const organizations = board.data?.organizations ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['on-prem-licences'] });

  const issueLicence = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const chosen = organizations.find((c) => c.keycloak_org_id === org);
      const res = await api.post('/global-admin/on-prem-licences/', {
        keycloak_org_id: org,
        organization_name: chosen?.name ?? '',
        expires_on: expires || null,
        notes,
      });
      return res.data as Licence;
    },
    onSuccess: () => {
      setIssuing(false);
      setOrg('');
      setExpires('');
      setNotes('');
      refresh();
      toast.success('Licence issued');
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const issueKey = useMutation({
    mutationFn: async (licence: Licence) => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.post(`/global-admin/on-prem-licences/${licence.id}/keys/`, {
        label: '',
      });
      return { key: res.data.key as string, who: licence.organization || licence.keycloak_org_id };
    },
    onSuccess: (result) => {
      setSecret(result);
      refresh();
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const revokeKey = useMutation({
    mutationFn: async ({ licence, key }: { licence: Licence; key: LicenceKey }) => {
      const api = await authenticatedBitswanBackendInstance();
      await api.delete(`/global-admin/on-prem-licences/${licence.id}/keys/${key.id}/`);
    },
    onSuccess: () => {
      refresh();
      toast.success('Key revoked');
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const revokeLicence = useMutation({
    mutationFn: async (licence: Licence) => {
      const api = await authenticatedBitswanBackendInstance();
      await api.delete(`/global-admin/on-prem-licences/${licence.id}/`);
    },
    onSuccess: () => {
      refresh();
      toast.success('Licence revoked; every key on it has stopped working');
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  return (
    <div className="space-y-6" data-testid="on-prem-aoc-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">On-prem AOC</h1>
          <p className="text-sm text-muted-foreground">
            Licences for an enterprise organisation to run an AOC on its own machine, and the
            keys that carry them.
          </p>
        </div>
        <Button onClick={() => setIssuing(true)} data-testid="issue-licence">
          Issue licence
        </Button>
      </div>

      {licences.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading licences…
        </div>
      ) : (licences.data ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No on-prem licences yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(licences.data ?? []).map((licence) => (
            <Card key={licence.id} data-testid={`licence-${licence.id}`}>
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                    {licence.organization || licence.keycloak_org_id}
                    <Badge className={STATUS_STYLE[licence.status]}>{licence.status}</Badge>
                  </CardTitle>
                  <CardDescription>
                    {licence.expires_on ? `Expires ${when(licence.expires_on)}` : 'Does not expire'}
                    {licence.notes ? ` · ${licence.notes}` : ''}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={licence.status !== 'active' || issueKey.isPending}
                    onClick={() => issueKey.mutate(licence)}
                    data-testid={`issue-key-${licence.id}`}
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    Issue key
                  </Button>
                  {licence.status !== 'revoked' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => revokeLicence.mutate(licence)}
                      data-testid={`revoke-licence-${licence.id}`}
                    >
                      <ShieldOff className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {licence.keys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No keys yet. Issue one for the customer to pull AOC images with.
                  </p>
                ) : (
                  <ul className="divide-y text-sm">
                    {licence.keys.map((key) => (
                      <li key={key.id} className="flex flex-wrap items-center gap-3 py-2">
                        <code className="rounded bg-muted px-2 py-0.5">{key.prefix}…</code>
                        {key.revoked ? (
                          <Badge className="border-red-200 bg-red-50 text-red-700">revoked</Badge>
                        ) : (
                          <span className="text-muted-foreground">
                            last used {when(key.last_used_at)}
                          </span>
                        )}
                        <span className="ml-auto text-muted-foreground">
                          issued {when(key.created_at)}
                        </span>
                        {!key.revoked && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => revokeKey.mutate({ licence, key })}
                            data-testid={`revoke-key-${key.id}`}
                          >
                            Revoke
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={issuing} onOpenChange={setIssuing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue an on-prem licence</DialogTitle>
            <DialogDescription>
              The customer can then be given one or more keys to pull AOC images and the CLI.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Enterprise organisation</Label>
              <SearchableSelect
                value={org}
                onChange={setOrg}
                placeholder="Choose an enterprise organisation"
                options={organizations.map((c) => ({
                  value: c.keycloak_org_id,
                  label: c.name || c.keycloak_org_id,
                }))}
                data-testid="licence-customer"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="licence-expires">Expires on</Label>
              <Input
                id="licence-expires"
                type="date"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                data-testid="licence-expires"
              />
              <p className="text-xs text-muted-foreground">Leave empty for a licence that does not expire.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="licence-notes">Notes</Label>
              <Input
                id="licence-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Contract reference, say"
                data-testid="licence-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIssuing(false)}>
              Cancel
            </Button>
            <Button
              disabled={!org || issueLicence.isPending}
              onClick={() => issueLicence.mutate()}
              data-testid="licence-save"
            >
              {issueLicence.isPending ? 'Issuing…' : 'Issue licence'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={secret !== null} onOpenChange={() => setSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>The key for {secret?.who}</DialogTitle>
            <DialogDescription>
              Copy it now. Only its first characters are stored, so it cannot be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-muted p-3 text-sm" data-testid="licence-key-secret">
              {secret?.key}
            </code>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigator.clipboard.writeText(secret?.key ?? '');
                toast.success('Copied');
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setSecret(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
